/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 필드셋 클래스를 정의한다.
 *
 * @file /src/components/form/field/grid/Grid.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 30.
 */
import * as parent from '@components/form/field/set/Set';
import { Component } from '@base/Component';
import { Language, LanguageText } from '@utils/Language';
import { Properties as Column } from '@models/table/Column';
import { List } from '@components/grid/List';
import { Grouper } from '@models/table/Group';
import { Record } from '@models/store/Record';
import { Local as Store } from '@models/store/list/Local';
import { Form } from '@components/form/Form';
import { Button } from '@namespaces/components/Button';
import { Renderer } from '@utils/Renderer';
import { Window } from '@components/window/Window';
import { Message } from '@components/message/Message';
import './Grid.css';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 그리드항목을 열 때
     * add 속성이 정의되어 있다면 항목수정윈도우가 열리며, 이 때 openItem 이벤트는 발생하지 않는다.
     */
    openItem?: (record: Record, me: Grid, grid: List) => void;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 필드값이 없을 경우 보일 placeHolder
     */
    placeholder?: LanguageText;

    /**
     * 필드정의
     */
    fields?: (string | { name: string; type: 'int' | 'float' | 'string' | 'boolean' | 'object' | 'date' })[];

    /**
     * 컬럼 목록
     */
    columns: Column<List>[];

    /**
     * 그룹정보
     */
    grouper?: Grouper<List>;

    /**
     * 고정 컬럼 수
     */
    freeze?: number;

    /**
     * 합계 설정 여부
     */
    summary?: boolean;

    /**
     * 그리드 고정높이
     */
    height?: number;

    /**
     * 그리드 최소높이
     */
    minHeight?: number;

    /**
     * 그리드 최소높이
     */
    maxHeight?: number;

    /**
     * 탑바에 추가될 항목
     */
    topbar?: (string | Component)[];

    /**
     * 탑바에 추가될 항목
     */
    bottombar?: (string | Component)[];

    /**
     * 데이터 정렬방식 (sortable 이 true 인 경우 정렬방식은 무시된다.)
     */
    sorters?: { [field: string]: 'ASC' | 'DESC' | any[] };

    /**
     * 정렬가능여부 (true 인 경우 컬럼의 정렬은 무시된다.)
     */
    sortable?: boolean;

    /**
     * 데이터추가폼
     */
    add?: (me: Grid, record: Record | null) => Form | Promise<Form>;

    /**
     * 삭제가능여부
     */
    deletable?: boolean | ((record: Record) => boolean);

    /**
     * 멀티 선택 여부
     */
    multiple?: boolean;

    /**
     * 이벤트 리스너
     */
    listeners?: Listeners<T>;
}

export class Grid extends parent.Set {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'set';

    /**
     * 그리드패널
     */
    panel: List = null;

    /**
     * 정렬가능여부 (true 인 경우 컬럼의 정렬은 무시된다.)
     */
    sortable: boolean;

    /**
     * 데이터추가함수
     */
    addFunction: ((data: { [key: string]: any } | null) => Promise<void>) | null = null;

    /**
     * 삭제가능여부
     */
    deletable: boolean | ((record: Record) => boolean);

    /**
     * 멀티 선택 여부
     */
    multiple: boolean;

    /**
     * 항목추가/선택항목삭제/정렬 버튼
     */
    actionButtons: Button[] = [];

    /**
     * 항목추가/선택항목삭제/정렬 및 항목열기 비활성화여부
     */
    disableActions: boolean = false;

    /**
     * 필드셋 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Omit<Properties<Grid>, 'items'> = null) {
        super(properties);

        this.sortable = this.properties.sortable === true;
        this.deletable = this.properties.deletable !== false ? (this.properties.deletable ?? false) : false;
        this.multiple = this.properties.multiple === true;

        this.addFunction = this.#createAdd(this.properties.add);
    }

    /**
     * 자식 컴포넌트를 초기화한다.
     */
    initItems(): void {
        this.items = [];
    }

    /**
     * 그리드패널을 가져온다.
     *
     * @returns 그리드패널
     */
    getPanel(): List {
        if (this.panel === null) {
            this.panel = new List({
                store: new Store({
                    provider: {},
                    fields: this.#getFields(),
                    sorters: this.sortable == true ? { sort: 'ASC' } : (this.properties.sorters ?? null),
                }),
                selection: {
                    selectable: this.sortable == true || this.deletable !== false || this.multiple === true,
                    multiple: this.sortable == true || this.deletable !== false || this.multiple === true,
                },
                columns: this.#getColumns(this.properties.columns ?? []),
                grouper: this.properties.grouper ?? null,
                freeze: this.properties.freeze ?? 0,
                summary: this.properties.summary === true,
                topbar: this.#getTools('top'),
                bottombar: this.#getTools('bottom'),
                height: this.properties.height ?? null,
                maxHeight: this.properties.maxHeight ?? null,
                minHeight: this.properties.minHeight ?? null,
                emptyText: this.properties.placeholder ?? null,
                listeners: {
                    edit: async (_, grid) => {
                        await this.setValue(grid.getData(), false, false);
                    },
                    update: async (grid) => {
                        if (this.isRendered() == true) {
                            await this.setValue(grid.getData(), false, false);
                        }
                    },
                    openItem: async (record, me) => {
                        if (this.disableActions == true) {
                            return;
                        }

                        if (this.addFunction !== null) {
                            this.addFunction(record);
                        } else {
                            await this.fireEvent('openItem', [record, this, me]);
                        }
                    },
                    openMenu: async (menu, selections) => {
                        if (this.disableActions == true) {
                            return;
                        }

                        if ((this.addFunction !== null && selections.length == 1) || this.deletable !== false) {
                            menu.setTitle(
                                Language.get('texts.selections_count', { count: selections.length.toString() })
                            );

                            if (this.addFunction !== null && selections.length == 1) {
                                menu.addMenu({
                                    icon: 'mi mi-form-edit',
                                    text: Language.get('components.form.field.grid.edit'),
                                    handler: async () => {
                                        this.addFunction(selections[0]);
                                        return true;
                                    },
                                });
                            }

                            if (this.deletable !== false) {
                                let isDeletable = this.deletable === true;
                                if (this.deletable !== true) {
                                    for (const record of selections) {
                                        if (this.deletable(record) === true) {
                                            isDeletable = true;
                                            break;
                                        }
                                    }
                                }

                                if (isDeletable == true) {
                                    menu.addMenu({
                                        icon: 'mi mi-trash',
                                        text: Language.get('components.form.field.grid.delete'),
                                        handler: async () => {
                                            await this.deleteValue();
                                            return true;
                                        },
                                    });
                                }
                            }
                        }
                    },
                },
            });
            this.panel.setParent(this);
        }

        return this.panel;
    }

    /**
     * 데이터스토어를 가져온다.
     *
     * @returns 데이터스토어
     */
    getStore(): Store {
        return this.getPanel().getStore() as Store;
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<{ [key: string]: any }[] | boolean> {
        if (this.isRendered() == false) {
            await this.render(true);
        }

        if (this.checkable == true && this.isChecked() == false) {
            return false;
        }

        return this.getPanel().getData();
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     * @param is_update - 데이터스토어 업데이트여부
     */
    async setValue(
        value: { [key: string]: any }[] | boolean,
        is_init: boolean = false,
        is_update: boolean = true
    ): Promise<void> {
        await this.lockValue();

        if (typeof value == 'boolean') {
            if (this.checkable == true) {
                this.onCheck(value);
            }

            if (this.collapsible == true) {
                if (value == true) {
                    this.expand();
                } else {
                    this.collapse();
                }
            }

            await this.releaseValue(value, is_init);
        } else {
            if (this.checkable == true) {
                this.onCheck(true);
            }

            if (is_update == true) {
                if (typeof value != 'boolean') {
                    await this.getStore().empty(false);

                    if (this.sortable == true) {
                        let index = 0;
                        for (const data of value) {
                            data.sort ??= index++;
                        }
                    }

                    if (this.sortable == true) {
                        await this.getStore().add(value, false);
                        await this.getStore().reindex('sort', false);
                    } else {
                        await this.getStore().add(value, false);
                    }
                }

                await this.releaseValue(value, is_init);
                await this.getStore().onLoad();
            } else {
                await this.releaseValue(value, is_init);
            }
        }
    }

    /**
     * 그리드패널에 데이터를 추가한다.
     *
     * @param data - 데이터
     * @param record_id - 기존 데이터 수정시 수정할 레코드고유값
     */
    async addValue(data: { [key: string]: any }, record_id: string = null): Promise<void> {
        if (this.isRendered() == false) {
            await this.render(true);
        }

        if (data === null || data === undefined) {
            return;
        }

        if (record_id !== null) {
            const record = this.getStore().get(record_id);
            await record.setData(data);
        } else {
            if (this.sortable == true) {
                data.sort ??= this.getStore().getCount();
                await this.getStore().add(data, false);
                await this.getStore().reindex('sort', true);
            } else {
                await this.getStore().add(data);
            }
        }
    }

    /**
     * 그리드패널에 데이터를 삭제한다.
     *
     * @param record_id - 삭제할 레코드고유값 (NULL 인 경우 그리드패널의 선택레코드)
     */
    async deleteValue(record_id: string | string[] = null): Promise<void> {
        if (this.isRendered() == false) {
            await this.render(true);
        }

        if (record_id !== null) {
            if (this.deletable === true) {
                await this.getStore().delete(record_id);
            } else if (this.deletable !== false) {
                const record_ids = Array.isArray(record_id) ? record_id : [record_id];
                for (const record_id of record_ids) {
                    const record = this.getStore().get(record_id);
                    if (this.deletable(record) == true) {
                        await this.getStore().delete(record);
                    }
                }
            }
        } else {
            const selections = this.getPanel().getSelection().getSelections();
            if (selections.length == 0) {
                Message.error({ code: 'REQUIRED_SELECTION' });
                return;
            }

            Message.delete({
                handler: async () => {
                    await this.getStore().delete(selections);
                    return true;
                },
            });
        }
    }

    /**
     * 읽기전용상태를 변경한다.
     *
     * @param readonly - 읽기전용여부
     */
    setReadonly(readonly: boolean): void {
        this.disableActions = readonly;
        this.actionButtons.forEach((button) => {
            button.setDisabled(readonly);
        });
        this.getPanel().setDisableEditor(readonly);

        super.setReadonly(readonly);
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @param disableActions - 항목추가/선택항목삭제/정렬 및 컬럼 에디터를 비활성화할지 여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean, disableActions: boolean = false): this {
        if (disableActions === true) {
            this.disableActions = disabled;
            this.actionButtons.forEach((button) => {
                button.setDisabled(disabled);
            });
            this.getPanel().setDisableEditor(disabled);
            return this;
        } else {
            this.getPanel().setDisabled(disabled);
            return super.setDisabled(disabled);
        }
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        await super.renderDom();

        this.$getComponent().setAttr('data-role', 'grid');
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getContent().append(this.getPanel().$getComponent());
        await Renderer.request(() => this.getPanel().render());
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        await super.renderState();
        await this.getStore().load();
    }

    /**
     * 필드셋 체크상태를 변경한다.
     *
     * @param checked - 체크여부
     * @param is_init - 초기값 여부
     */
    onCheck(checked: boolean, is_init: boolean = false): void {
        super.onCheck(checked, is_init);
        this.getPanel().setDisabled(checked == false);
    }

    /**
     * 데이터추가 함수를 생성한다.
     */
    #createAdd(
        add: (me: Grid, record: Record | null) => Form | Promise<Form>
    ): ((data: { [key: string]: any } | null) => Promise<void>) | null {
        if (typeof add == 'function') {
            return async (record: Record | null): Promise<void> => {
                const form = await add(this, record);
                new Window({
                    title:
                        record === null
                            ? Language.get('components.form.field.grid.add')
                            : Language.get('components.form.field.grid.edit'),
                    items: [form],
                    modal: true,
                    closable: false,
                    buttons: [
                        new Button({
                            text: Language.get('buttons.cancel'),
                            handler: (button) => {
                                const window = button.getParent().getParent() as Window;
                                window.close();
                            },
                        }),
                        new Button({
                            text: Language.get('buttons.ok'),
                            buttonClass: 'confirm',
                            handler: async (button) => {
                                const window = button.getParent().getParent() as Window;
                                const form = window.getItemAt(0) as Form;

                                if ((await form.isValid()) == true) {
                                    await this.addValue(await form.getValues(), record?.getId() ?? null);
                                    window.close();
                                }
                            },
                        }),
                    ],
                    listeners: {
                        show: async (window) => {
                            if (record !== null) {
                                window.setDisabled(true);
                                const form = window.getItemAt(0) as Form;
                                await form.setValues(record.getData(), true);
                                window.setDisabled(false);
                            }
                        },
                    },
                }).show();
            };
        }

        return null;
    }

    /**
     * 그리드필드의 속성에 따라 데이터스토어의 필드정보를 가져온다.
     *
     * @returns 필드정보
     */
    #getFields(): (string | { name: string; type: 'int' | 'float' | 'string' | 'boolean' | 'object' | 'date' })[] {
        const fields = this.properties?.fields ?? [];

        if (this.sortable == true) {
            fields.push({ name: 'sort', type: 'int' });
        }

        return fields;
    }

    /**
     * 그리드필드의 속성에 따라 그리드패널의 컬럼속성을 변경한다.
     *
     * @param columns - 변경할 원본 컬럼목록
     * @returns 속성이 변경된 컬럼목록
     */
    #getColumns(columns: Column<List>[]): Column<List>[] {
        /**
         * 정렬을 사용할 경우 컬럼의 정렬속성을 제거하고, 정렬을 위한 옵션을 추가한다.
         */
        if (this.sortable == true) {
            for (const column of columns) {
                if (column.columns !== undefined) {
                    column.columns = this.#getColumns(column.columns);
                }

                if (column.sortable !== undefined) {
                    column.sortable = false;
                }
            }
        }

        return columns;
    }

    /**
     * 그리드패널의 툴바아이템을 가져온다.
     *
     * @param position - 가져올 툴바위치
     * @returns 툴바 아이템
     */
    #getTools(position: 'top' | 'bottom'): (string | Component)[] | null {
        let tools: (string | Component)[] = [];

        if (position == 'top') {
            let isRight = false;
            const topbar = this.properties.topbar ?? [];
            for (const top of topbar) {
                if (top == '->') {
                    isRight = true;
                }

                tools.push(top);
            }

            if (isRight == false && (this.addFunction !== null || this.deletable !== false)) {
                tools.push('->');
            }

            if (this.addFunction !== null) {
                const addButton = new Button({
                    icon: 'mi mi-plus',
                    text: Language.get('components.form.field.grid.add'),
                    handler: () => {
                        this.addFunction(null);
                    },
                });
                this.actionButtons.push(addButton);
                tools.push(addButton);
            }

            if (this.deletable !== false) {
                const deleteButton = new Button({
                    icon: 'mi mi-minus',
                    text: Language.get('components.form.field.grid.delete'),
                    handler: async () => {
                        await this.deleteValue();
                    },
                });
                this.actionButtons.push(deleteButton);
                tools.push(deleteButton);
            }
        }

        if (position == 'bottom') {
            if (this.sortable == true) {
                const upButton = new Button({
                    icon: 'mi mi-caret-up',
                    handler: async (button) => {
                        button.setLoading(true);
                        await this.getPanel().moveSelections('up');
                        button.setLoading(false);
                    },
                });
                const downButton = new Button({
                    icon: 'mi mi-caret-down',
                    handler: async (button) => {
                        button.setLoading(true);
                        await this.getPanel().moveSelections('down');
                        button.setLoading(false);
                    },
                });
                this.actionButtons.push(upButton, downButton);
                tools.push(upButton, downButton);
            }

            const bottombar = this.properties.bottombar ?? [];
            for (const bottom of bottombar) {
                tools.push(bottom);
            }
        }

        return tools.length > 0 ? tools : null;
    }
}
